import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ExternalId,
  ILabelDetail,
  ILabelNotificationEmail,
} from '../../../data/models/label';
import './LabelInfoModal.scss';

const _defaultEmailNotificationLanguage = 'en';

interface LabelInfoModalProps {
  togglePopUp: () => void;
  label: ILabelDetail;
  isParentLabel?: boolean;
}

export default function LabelInfoModal({
  togglePopUp,
  label,
  isParentLabel,
}: LabelInfoModalProps) {
  const { t } = useTranslation();
  const [showPopUp, setShowPopUp] = useState(true);

  const formatValue = (value: string | undefined) => {
    if (value) {
      return <span title={value}>{value}</span>;
    }
    return <span className="placeholder-value">{'---'}</span>;
  };

  const formatBooleanValue = (value: boolean | undefined) => {
    if (value) {
      return formatValue(t('product.yes'));
    }
    return formatValue(t('product.no'));
  };

  const formatEnumValue = (
    enumValue: string | undefined,
    translationKey: string
  ) => {
    if (enumValue) {
      return formatValue(t(`${translationKey}_${enumValue}`));
    }
    return formatValue(enumValue);
  };

  const formatEmailList = (emails?: string[]) => {
    return (
      <span className="value col-8">
        {emails?.length ? (
          emails.map((email, index) => {
            return <div key={index}>{formatValue(email)}</div>;
          })
        ) : (
          <span className="placeholder-value">{'---'}</span>
        )}
      </span>
    );
  };

  const formatPiasLabelCode = (externalIds?: ExternalId[]) => {
    const piasLabelCode = externalIds?.find(
      (e) => e.type?.trim().toUpperCase() === 'PIAS_MUSIC_LABEL.LABEL_CODE'
    )?.value;

    return (
      <span className="value col-8">
        {piasLabelCode ? (
          <span title={piasLabelCode}>{piasLabelCode}</span>
        ) : (
          <span className="placeholder-value">{'---'}</span>
        )}
      </span>
    );
  };

  const formatNotificationEmail = (email: ILabelNotificationEmail) => {
    return (
      <span className="value">
        <span>{email.address}</span>
        <span className="email-language">{`(${email.language ?? _defaultEmailNotificationLanguage})`}</span>
      </span>
    );
  };

  const closePopUp = () => {
    setShowPopUp(false);
    togglePopUp();
  };

  useEffect(() => {
    const keyDownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closePopUp();
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [closePopUp]);

  const modalTitle = isParentLabel
    ? t('product.label.parentLabelInfo')
    : t('product.label.labelInfo');

  return (
    <>
      {showPopUp ? (
        <div id="labelInfoModal" className="product-edit-mode-modal">
          <div className="product-edit-mode-modal-content">
            <div className="product-edit-mode-modal-header">
              <button
                className="btn-close"
                type="button"
                aria-label="Close"
                onClick={closePopUp}
              >
                <i className="icon fas fa-times fa-lg" aria-hidden="true"></i>
              </button>
              <h2>{modalTitle}</h2>
              <div className="edit-modal-spacer"></div>
            </div>

            <div className="product-edit-mode-modal-body label-info-modal-content">
              <div className="scroll-body">
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.editProduct.labelname')}
                  </label>
                  <span className="value col-8">{formatValue(label.name)}</span>
                </div>
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.label.labelId')}
                  </label>
                  <span className="value col-8">
                    {formatValue(label.indbOrgId?.toString())}
                  </span>
                </div>
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.label.parentLabel')}
                  </label>
                  <span className="value col-8">
                    {formatValue(label.parentOrgName)}
                  </span>
                </div>
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.label.parentLabelId')}
                  </label>
                  <span className="value col-8">
                    {formatValue(label.parentOrgId?.toString())}
                  </span>
                </div>
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.label.dealType')}
                  </label>
                  <span className="value col-8">
                    {formatEnumValue(label.dealType, 'product.label.dealType')}
                  </span>
                </div>
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.label.digitalRights')}
                  </label>
                  <span className="value col-8">
                    {formatBooleanValue(label.digitalRights)}
                  </span>
                </div>
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.label.physicalRights')}
                  </label>
                  <span className="value col-8">
                    {formatBooleanValue(label.physicalRights)}
                  </span>
                </div>
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.label.syncRights')}
                  </label>
                  <span className="value col-8">
                    {formatBooleanValue(label.syncRights)}
                  </span>
                </div>
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.label.mechanicalProcessing')}
                  </label>
                  <span className="value col-8">
                    {formatEnumValue(
                      label.mechanicalProcessing,
                      'product.label.mechanicals'
                    )}
                  </span>
                </div>
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.label.soundExchange')}
                  </label>
                  <span className="value col-8">
                    {formatBooleanValue(label.soundExchange)}
                  </span>
                </div>
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.label.neighboringRights')}
                  </label>
                  <span className="value col-8">
                    {formatBooleanValue(label.neighboringRights)}
                  </span>
                </div>
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.label.isrcAssignmentPreference')}
                  </label>
                  <span className="value col-8">
                    {formatEnumValue(
                      label.isrcAssignmentPreference,
                      'product.label.isrc'
                    )}
                  </span>
                </div>
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.label.labelRank')}
                  </label>
                  <span className="value col-8">
                    {formatValue(label.labelRank?.toString())}
                  </span>
                </div>
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.label.financialLabelRank')}
                  </label>
                  <span className="value col-8">
                    {formatValue(label.financialLabelRank?.toString())}
                  </span>
                </div>
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.label.labelManagerEmail')}
                  </label>
                  {formatEmailList(label.labelManagerEmail)}
                </div>
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.label.marketingManagerEmail')}
                  </label>
                  {formatEmailList(label.marketingManagerEmail)}
                </div>
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.label.productionManagerEmail')}
                  </label>
                  {formatEmailList(label.productionManagerEmail)}
                </div>
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.label.labelNotificationEmails')}
                  </label>
                  <span className="value col-8">
                    {label.labelNotificationEmails?.length ? (
                      label.labelNotificationEmails?.map(
                        (lne: ILabelNotificationEmail, index: number) => {
                          return (
                            <div key={index}>
                              {formatNotificationEmail(lne)}
                            </div>
                          );
                        }
                      )
                    ) : (
                      <span className="placeholder-value">{'---'}</span>
                    )}
                  </span>
                </div>
                <div className="label-info-row">
                  <label className="col-4 detail-label">
                    {t('product.label.piasLabelCode')}
                  </label>
                  {formatPiasLabelCode(label.externalIds)}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <div className="edit-modal-spacer"></div>
              <div className="modal-footer-buttons">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closePopUp}
                >
                  {t('common.button.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
